import type { ReactNode } from 'react';
import React, { useId, useState } from 'react';
import { isInteger, noop } from 'lodash';
import { cleanupId } from 'Utils/unique-id/uniqueId';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl';
import type { IconName } from 'Components/frameworks/icon/Icon';
import 'Components/frameworks/icon/Icon';
import { themes } from './InputText.themes';
type InputTypes = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';
type Props = {
  autoComplete?: AutoComplete;
  className?: string;
  dataTestId?: string;
  hasClear?: boolean;
  hasValidationIcon?: boolean;
  iconLeft?: IconName;
  id?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string | Node;
  maxLength?: number;
  minLength?: number;
  name?: string;
  notes?: string;
  onBlur?: (arg0: React.SyntheticEvent) => void;
  onChange?: (name: string, value: string) => void;
  onClear?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onFocus?: (arg0: React.SyntheticEvent) => void;
  onKeyDown?: (arg0: React.KeyboardEvent) => void;
  onKeyUp?: (arg0: React.KeyboardEvent) => void;
  pattern?: string;
  placeholder?: string;
  theme?: keyof typeof themes;
  type?: InputTypes;
  validationState?: string;
  value?: string;
};
type State = {
  value: string;
};
export function InputText({
  autoComplete = 'off',
  className,
  dataTestId,
  hasClear,
  hasValidationIcon,
  iconLeft,
  id: idProp,
  isActive,
  isDisabled,
  isFilled,
  isLoading,
  isReadOnly,
  isRequired,
  label,
  maxLength,
  minLength,
  name,
  notes,
  onBlur,
  onChange = noop,
  onClear: onClearProp = noop,
  onClick,
  onFocus,
  onKeyDown: onKeyDownProp = noop,
  onKeyUp,
  pattern,
  placeholder,
  theme = 'default',
  type = 'text',
  validationState,
  value: valueProp,
}: Props) {
  const [value, setValue] = useState(valueProp || '');

  // Sync internal state from prop during render (replaces UNSAFE_componentWillReceiveProps).
  // This runs synchronously during render, avoiding the extra render cycle that useEffect
  // would cause — critical for preserving the batching behavior of the class component.
  const [prevValueProp, setPrevValueProp] = useState(valueProp);
  if (valueProp !== prevValueProp) {
    setPrevValueProp(valueProp);
    setValue(valueProp ?? '');
  }

  const reactId = useId();
  const labelStr = typeof label === 'string' ? cleanupId(label) : '';
  const id = idProp ?? (labelStr ? `InputText_${labelStr}` : `InputText_${reactId}`);

  function handleChange(e: React.SyntheticEvent) {
    if (!isReadOnly && !isLoading) {
      // @ts-expect-error TODO(flow-migration)
      const { value: newValue } = e.target;
      setValue(newValue);
      onChange(name ?? '', newValue);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    onKeyDownProp(e);
  }

  function handleClear(e: React.SyntheticEvent) {
    setValue('');
    onClearProp(e);
  }

  const hasClearEnabled = Boolean(hasClear && value);
  const hasValue = Boolean(value);
  const { thInput } = themes[theme];
  return (
    <FormControl
      className={className}
      hasClear={hasClearEnabled}
      hasValidationIcon={hasValidationIcon}
      iconLeft={iconLeft}
      id={id}
      isActive={isActive}
      isDisabled={isDisabled}
      isFilled={isFilled && hasValue}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      label={label}
      notes={notes}
      onClear={handleClear}
      theme={theme}
      validationState={validationState}
    >
      <input
        autoComplete={autoComplete}
        className={thInput}
        disabled={isDisabled}
        data-testid={dataTestId}
        id={id}
        {...(isInteger(maxLength)
          ? {
              maxLength,
            }
          : null)}
        {...(isInteger(minLength)
          ? {
              minLength,
            }
          : null)}
        {...(name
          ? {
              name,
            }
          : null)}
        onBlur={onBlur}
        onChange={handleChange}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={onKeyUp}
        {...(pattern
          ? {
              pattern,
            }
          : null)}
        {...(placeholder
          ? {
              placeholder: getFormattedLabel(placeholder, isRequired),
            }
          : null)}
        readOnly={Boolean(isReadOnly || isLoading)}
        required={isRequired}
        role={type === 'search' ? 'search' : 'textbox'}
        type={type}
        value={value}
      />
    </FormControl>
  );
}
